import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { usePresenceSnapshot } from '@/hooks/usePresenceWebSocket';
import { nominalsService, userService } from '@/services';
import type { User } from '@/types/api';
import type { Nominal } from '@/types/nominals';
import {
  createDefaultNominalsFilters,
  createDefaultUsersFilters,
} from '@/features/addressBook/addressBookShared';
import { isUserOnline } from '@/features/onlineContacts/presenceForUserId';
import { contactMatchesSearch } from '@/features/onlineContacts/onlineContactsSearch';
import { sortByPresenceThenLabel } from '@/features/onlineContacts/sortByPresence';
import {
  managementNameLabel,
  personDisplayName,
} from '@/features/userManagement/userManagementUtils';

export type OnlineContactsView = 'users' | 'nominals';

const LIST_PAGE_SIZE = 100;

type UseOnlineContactsPanelOptions = {
  user: User | null;
  t: TFunction;
};

export function useOnlineContactsPanel({ user, t }: UseOnlineContactsPanelOptions) {
  const [activeView, setActiveView] = useState<OnlineContactsView>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [nominals, setNominals] = useState<Nominal[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [nominalsLoading, setNominalsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { presenceByUserId, isConnected, refetchPresenceSnapshot } = usePresenceSnapshot(
    Boolean(user?.id)
  );

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setError(null);
    try {
      const data = await userService.getUsers({
        ...createDefaultUsersFilters(),
        page: 1,
        page_size: LIST_PAGE_SIZE,
        is_active: true,
      });
      setUsers(Array.isArray(data.results) ? data.results : []);
    } catch {
      setError(t('addressBook.failedToLoadUsers'));
      setUsers([]);
    } finally {
      setUsersLoading(false);
      void refetchPresenceSnapshot();
    }
  }, [refetchPresenceSnapshot, t]);

  const fetchNominals = useCallback(async () => {
    setNominalsLoading(true);
    setError(null);
    try {
      const filters = {
        ...createDefaultNominalsFilters(),
        page: 1,
        page_size: LIST_PAGE_SIZE,
        is_active: true,
      };
      if (!user?.is_superuser && user?.establishment?.id) {
        filters.establishment_id = user.establishment.id;
      }
      const data = await nominalsService.getNominals(filters);
      setNominals(Array.isArray(data.results) ? data.results : []);
    } catch {
      setError(t('addressBook.failedToLoadNominals'));
      setNominals([]);
    } finally {
      setNominalsLoading(false);
      void refetchPresenceSnapshot();
    }
  }, [refetchPresenceSnapshot, t, user?.establishment?.id, user?.is_superuser]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    void fetchUsers();
    void fetchNominals();
  }, [user?.id, fetchUsers, fetchNominals]);

  const onlineUsers = useMemo(
    () =>
      sortByPresenceThenLabel(
        users.filter(
          row =>
            String(row.id) !== String(user?.id) &&
            isUserOnline(presenceByUserId, row.id)
        ),
        presenceByUserId,
        item => managementNameLabel(item)
      ),
    [users, presenceByUserId, user?.id]
  );

  const onlineNominals = useMemo(
    () =>
      sortByPresenceThenLabel(
        nominals.filter(
          row =>
            String(row.id) !== String(user?.id) &&
            isUserOnline(presenceByUserId, row.id)
        ),
        presenceByUserId,
        item => personDisplayName(item, item.username)
      ),
    [nominals, presenceByUserId, user?.id]
  );

  const filteredUsers = useMemo(
    () =>
      onlineUsers.filter(row =>
        contactMatchesSearch(
          [
            managementNameLabel(row),
            row.username,
            row.freeswitch_extension,
            row.first_name,
            row.last_name,
          ],
          searchQuery
        )
      ),
    [onlineUsers, searchQuery]
  );

  const filteredNominals = useMemo(
    () =>
      onlineNominals.filter(row =>
        contactMatchesSearch(
          [
            personDisplayName(row, row.username),
            row.username,
            row.prisoner_number,
          ],
          searchQuery
        )
      ),
    [onlineNominals, searchQuery]
  );

  const loading = activeView === 'users' ? usersLoading : nominalsLoading;
  const onlineCount =
    activeView === 'users' ? onlineUsers.length : onlineNominals.length;

  const setActiveViewAndClearSearch = useCallback((view: OnlineContactsView) => {
    setSearchQuery('');
    setActiveView(view);
  }, []);

  return {
    activeView,
    setActiveView: setActiveViewAndClearSearch,
    filteredUsers,
    filteredNominals,
    presenceByUserId,
    isConnected,
    loading,
    error,
    onlineCount,
    searchQuery,
    setSearchQuery,
    usersCount: onlineUsers.length,
    nominalsCount: onlineNominals.length,
  };
}
